import React from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { signOut as signOutFromMsal } from '@/auth/msal';
import { BRAND_LOGO_SRC, BRAND_NAME } from '@/branding';
import { UnifiedHeader } from '@/components';
import {
  Button,
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarSeparator,
  SidebarTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui';
import { useSession } from '@/session';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';

type AppShellProps = {
  showNav?: boolean;
};

type SidebarNavItem = {
  label: string;
  to: string;
  icon: string;
};

export default function AppShell({ showNav = true }: AppShellProps) {
  const location = useLocation();
  const { profile, isSystemAdmin } = useSession();

  const navItems = React.useMemo<SidebarNavItem[]>(() => {
    const items: SidebarNavItem[] = [
      { label: 'Overview', to: '/app', icon: 'dashboard' },
      { label: 'Calls', to: '/calls', icon: 'call' },
    ];
    if (isSystemAdmin) {
      items.push({ label: 'Ops', to: '/ops', icon: 'analytics' });
    }
    return items;
  }, [isSystemAdmin]);

  const settingsItem = React.useMemo<SidebarNavItem>(() => {
    return { label: 'Settings', to: '/settings', icon: 'settings' };
  }, []);

  async function handleSignOut() {
    await signOutFromMsal();
  }

  if (!showNav) {
    return (
      <div className="min-h-screen bg-surfaceAlt text-ink flex flex-col">
        <UnifiedHeader showNav={false} />
        <Outlet />
      </div>
    );
  }

  return (
    <SidebarProvider className="bg-surfaceAlt text-ink">
      <Sidebar variant="inset">
        <SidebarHeader className="p-4 cursor-default flex flex-col items-center gap-2 text-center">
          <img
            src={BRAND_LOGO_SRC}
            alt={BRAND_NAME}
            className="w-10 h-10 object-contain"
          />
          <div className="w-full min-w-0 flex flex-col items-center leading-tight">
            <span className="text-[16px] font-extrabold tracking-[-0.01em]">{BRAND_NAME}</span>
          </div>
        </SidebarHeader>

        <SidebarSeparator />

        <SidebarContent>
          <SidebarGroup>
            <SidebarGroupContent>
              <SidebarMenu>
                {navItems.map((item) => {
                  const isExactMatch = location.pathname === item.to;
                  const isOpsSubpage = item.to === '/ops' && location.pathname.startsWith('/ops/');
                  const isActiveItem = isExactMatch || isOpsSubpage;

                  return (
                    <SidebarMenuItem key={item.to}>
                      <SidebarMenuButton asChild isActive={isActiveItem} tooltip={item.label}>
                        <Link
                          to={item.to}
                          aria-current={isActiveItem ? 'page' : undefined}
                        >
                          <span aria-hidden className="material-symbols-outlined text-[18px] leading-none">
                            {item.icon}
                          </span>
                          <span>{item.label}</span>
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </SidebarContent>


        <div className="p-2">
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton
                asChild
                isActive={location.pathname === settingsItem.to}
                tooltip={settingsItem.label}
              >
                <Link
                  to={settingsItem.to}
                  aria-current={location.pathname === settingsItem.to ? 'page' : undefined}
                >
                  <span aria-hidden className="material-symbols-outlined text-[18px] leading-none">
                    {settingsItem.icon}
                  </span>
                  <span>{settingsItem.label}</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </div>

        <SidebarSeparator />

        <SidebarFooter className="">
          <SidebarMenu>
            <Tooltip>
              <TooltipTrigger asChild>
                <SidebarMenuItem>
                  <SidebarMenuButton onClick={handleSignOut} tooltip="Sign out">
                    <Avatar className="h-8 w-8 rounded-lg">
                      <span aria-hidden className="material-symbols-outlined text-[28px]    leading-none">logout</span>
                    </Avatar>
                    <div className="grid flex-1 text-left text-sm leading-tight">
                      <span className="truncate font-medium">{profile?.name}</span>
                      <span className="truncate text-xs">{profile?.email}</span>
                    </div>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </TooltipTrigger>
              <TooltipContent>
                Sign out
              </TooltipContent>
            </Tooltip>
          </SidebarMenu>
        </SidebarFooter>
      </Sidebar>

      <SidebarInset className="bg-surfaceAlt">
        <div className="md:hidden sticky top-0 z-10 border-b border-border/90 bg-surfaceAlt/90 backdrop-blur-md px-3 py-2">
          <SidebarTrigger />
        </div>
        <Outlet />
      </SidebarInset>
    </SidebarProvider>
  );
}
